import dayjs from 'dayjs';
import { useLayoutEffect, useRef, useState } from 'react';

const FORMATS_ORDER = ['xl', 'l', 'm', 's'];

const buildFormatsMapping = (dateToShow) => {
  const sameYear = dayjs().isSame(dateToShow, 'year');
  return {
    s: sameYear ? 'DD MMM' : 'DD MMM YYYY',
    m: sameYear ? 'Do MMM' : 'Do MMM YYYY',
    l: sameYear ? 'Do MMMM' : 'Do MMMM, YYYY',
    xl: sameYear ? 'dddd, Do MMMM' : 'dddd, Do MMMM YYYY',
  };
};

const useAdaptiveDateDisplay = ({
  dateToShow,
  mobile,
  containerRef,
  titleRef,
  actionsRef,
  leftPadding,
  rightPadding = 45,
  mobileContainerPadding = 44,
  desktopContainerPadding = 60,
  buttonMarginRight = 16,
  extraDependencies = [],
}) => {
  const buttonRef = useRef(null);
  const [selectedFormat, setSelectedFormat] = useState('xl');
  const formatsMapping = buildFormatsMapping(dateToShow);

  useLayoutEffect(() => {
    const determineFormat = () => {
      const headerElem = containerRef?.current;
      const headerWidth = headerElem?.offsetWidth ?? 0;

      const titleWidth = titleRef?.current?.offsetWidth ?? 0;
      const actionElementsWidth = actionsRef?.current?.offsetWidth ?? 0;

      const availableWidth = mobile
        ? headerWidth - leftPadding - rightPadding - mobileContainerPadding - buttonMarginRight
        : headerWidth -
          titleWidth -
          actionElementsWidth -
          leftPadding -
          rightPadding -
          desktopContainerPadding -
          buttonMarginRight;

      const testElement = document.createElement('span');
      testElement.style.visibility = 'hidden';
      testElement.style.position = 'absolute';
      testElement.style.whiteSpace = 'nowrap';
      document.body.appendChild(testElement);

      let chosenFormat = 's';

      for (const format of FORMATS_ORDER) {
        testElement.textContent = dayjs(dateToShow).format(formatsMapping[format]);
        if (testElement.offsetWidth <= availableWidth) {
          chosenFormat = format;
          break;
        }
      }

      document.body.removeChild(testElement);
      setSelectedFormat(chosenFormat);
    };

    determineFormat();

    const resizeObserver = new ResizeObserver(determineFormat);
    [buttonRef.current, containerRef?.current, titleRef?.current, actionsRef?.current].forEach((element) => {
      if (element) {
        resizeObserver.observe(element);
      }
    });

    window.addEventListener('resize', determineFormat);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', determineFormat);
    };
  }, [
    dateToShow,
    mobile,
    containerRef,
    titleRef,
    actionsRef,
    leftPadding,
    rightPadding,
    mobileContainerPadding,
    desktopContainerPadding,
    buttonMarginRight,
    ...extraDependencies,
  ]);

  return {
    buttonRef,
    selectedFormat,
    formatsMapping,
  };
};

export default useAdaptiveDateDisplay;
